import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";

import { crmPostventaFichaApi } from "./crmPostventaFichaApi";
import { formatCuit, todayIso } from "../crmpostventa-shared/crmFormat";
import useIsMobile from "../crmpostventa-shared/useIsMobile";
import { LoadError } from "../crmpostventa-shared/components/EmptyState";
import FichaHeader from "./components/FichaHeader";
import TerminalTabs from "./components/TerminalTabs";
import FichaCard from "./components/FichaCard";
import HistorialResumen from "./components/HistorialResumen";
import HistorialCompletoPanel from "./components/historial/HistorialCompletoPanel";
import FichaSkeleton, { CardsSkeleton } from "./components/FichaSkeleton";
import FichaEditorModal from "./editor/FichaEditorModal";
import { GESTION_FINALIZADA_TITULO, fichaGestionRegistradaToast, fichaSavedToast } from "./editor/fichaEditorModel";
import { fichaGestionSavedToast } from "../crmpostventa-ipc/fichaGestion";
import { gestionHref } from "../crmpostventa-shared/crmLinks";
import "../crmpostventa-rollo/rollo.css";
import "./ficha.css";

const LOAD_ERROR_MESSAGE = "Hubo un problema de conexión con el servidor. Probá de nuevo en unos segundos.";

// 403 and 404 carry a message for the asesor ("No se encontró el cliente solicitado."); anything else is a connection
// problem.
const loadErrorMessage = (error) => (error?.status === 403 || error?.status === 404) && error.message ? error.message : LOAD_ERROR_MESSAGE;

/**
 * CRM Postventa › Ficha del cliente in consulta mode (T6 of odd/tasks/ficha-cliente.md, S11; design
 * docs/crm-postventa-ficha/design/prototipo-ficha-cliente.html, Prompt 1). It loads GET api/crm-postventa-ficha/{clienteId}
 * and shows the header, the terminal tabs, the four cards and the history summary.
 *
 * The container (CrmPostventa.js) owns the active comercio: clienteId is the terminal to show (IdAlta), a tab click hands
 * the chosen terminal to onCambiarTerminal, and reloadKey changes after a save elsewhere. onVerHistorial(seccion?) opens
 * the full history (filtered by block when a card footer asks).
 *
 * "Editar datos" (enabled by the server's puedeEditar) opens the editor (T7, Prompts 2 and 3) with the container's
 * catalogs (fichaCatalogos, shared with Altas/Bajas). editarAlCargar ("Editar datos" of a gestión, &editar=1) opens it
 * once this terminal is loaded and then calls onEditarConsumido, so the container drops &editar=1 from the URL.
 * gestionContext (useFichaGestionContext) adds the gestión notice; its save finalizes the gestión and the toast says so
 * with "Volver a la gestión". An informal save that registered a gestión shows "Ver gestión" to the new one (T11, S12).
 * After a save the panel reloads, highlights the new history entry and calls onGuardado(response).
 */
export default function FichaPanel({
  clienteId,
  darkMode = false,
  reloadKey = 0,
  catalogos,
  gestionContext,
  editarAlCargar = false,
  editarButtonRef,
  onEditarConsumido,
  onGuardado,
  onCambiarTerminal,
  onCambiarCliente,
  onRefrescar,
  onNavigate,
}) {
  const isMobile = useIsMobile();
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [nuevoId, setNuevoId] = useState(null);
  // Full history panel: null when closed, else the section it opened filtered by ("" = all sections).
  const [historial, setHistorial] = useState(null);
  // The editor previews the alícuota with the ficha's map, the same values the save stores.
  const catalogosEditor = useMemo(() => ({ ...catalogos, alicuotasPorProvincia: ficha?.alicuotasPorProvincia }), [catalogos, ficha]);
  const verHistorial = useCallback((seccion) => setHistorial({ seccion: seccion || "" }), []);
  const requestRef = useRef(0);
  const autoEditRef = useRef(null);

  const load = useCallback(async () => {
    requestRef.current += 1;
    const request = requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await crmPostventaFichaApi.obtenerFicha(clienteId);
      if (request === requestRef.current) setFicha(response);
    } catch (loadError) {
      if (request === requestRef.current) setError(loadError);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    if (clienteId) load();
    return () => {
      // A late answer of this terminal never replaces the next one (or lands after unmount).
      requestRef.current += 1;
    };
  }, [load, reloadKey, clienteId]);

  // &editar=1: open the editor once, when the ficha of this terminal is loaded, and let the container drop the flag.
  useEffect(() => {
    if (!editarAlCargar || loading || !ficha || String(ficha.clienteId) !== String(clienteId)) return;
    if (autoEditRef.current === String(clienteId)) return;
    autoEditRef.current = String(clienteId);
    if (ficha.puedeEditar) setEditorOpen(true);
    onEditarConsumido?.();
  }, [clienteId, editarAlCargar, ficha, loading, onEditarConsumido]);

  const handleSaved = (response) => {
    const cabecera = ficha?.cabecera;
    setEditorOpen(false);
    setNuevoId(response?.cambioId ? `ficha-${response.cambioId}` : null);
    load();
    onGuardado?.(response);
    if (gestionContext?.gestionId) {
      const returnHref = gestionContext.returnHref;
      const toast = fichaGestionSavedToast(gestionContext, cabecera);
      Promise.resolve(Swal.fire(response?.gestionId ? { ...toast, title: GESTION_FINALIZADA_TITULO } : toast)).then((choice) => {
        if (choice?.isConfirmed && returnHref) onNavigate?.(returnHref);
      });
    } else if (response?.gestionId) {
      const href = gestionHref({ clienteId: ficha?.clienteId ?? clienteId, gestionId: response.gestionId });
      Promise.resolve(Swal.fire(fichaGestionRegistradaToast(cabecera))).then((choice) => {
        if (choice?.isConfirmed) onNavigate?.(href);
      });
    } else {
      Swal.fire(fichaSavedToast(cabecera));
    }
  };

  // The "Nueva" mark belongs to the terminal that was saved.
  useEffect(() => {
    setNuevoId(null);
  }, [clienteId]);

  const titulos = useMemo(() => {
    const map = new Map((ficha?.secciones || []).map((seccion) => [seccion.clave, seccion.titulo]));
    return (clave) => map.get(clave) || "";
  }, [ficha]);

  const refrescar = () => {
    load();
    onRefrescar?.();
  };

  const className = `crm-rollo crm-ficha${darkMode ? " is-dark" : ""}${isMobile ? " is-mobile" : ""}`;

  const renderBody = () => {
    if (error) {
      return <LoadError title="No pudimos cargar la ficha" message={loadErrorMessage(error)} onRetry={load} />;
    }
    if (!ficha) return <FichaSkeleton />;

    const canEdit = Boolean(ficha.puedeEditar);
    return (
      <>
        <FichaHeader
          cabecera={ficha.cabecera}
          clienteId={ficha.clienteId}
          canEdit={canEdit}
          busy={loading}
          darkMode={darkMode}
          editarButtonRef={editarButtonRef}
          onEditar={() => setEditorOpen(true)}
          onRefrescar={refrescar}
          onCambiarCliente={onCambiarCliente}
          onNavigate={onNavigate}
        />
        <TerminalTabs terminales={ficha.terminales} activeId={clienteId} onSelect={onCambiarTerminal} />
        {loading ? (
          <CardsSkeleton />
        ) : (
          <>
            <div className="crm-ficha__grid">
              {(ficha.secciones || []).map((seccion) => (
                <FichaCard
                  key={seccion.clave}
                  seccion={seccion}
                  clienteId={ficha.clienteId}
                  onVerHistorial={verHistorial}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
            <HistorialResumen
              items={ficha.ultimosCambios}
              isMobile={isMobile}
              tituloDe={titulos}
              clienteId={ficha.clienteId}
              today={todayIso()}
              nuevoId={nuevoId}
              onVerHistorial={verHistorial}
              onNavigate={onNavigate}
            />
          </>
        )}
      </>
    );
  };

  return (
    <section className={className} aria-label="Ficha del cliente" aria-busy={loading && !error ? "true" : undefined}>
      {renderBody()}
      {editorOpen && ficha ? (
        <FichaEditorModal
          ficha={ficha}
          catalogos={catalogosEditor}
          gestionContext={gestionContext}
          darkMode={darkMode}
          returnFocusRef={editarButtonRef}
          onClose={() => setEditorOpen(false)}
          onSaved={handleSaved}
          onFichaRecargada={setFicha}
          onNavigate={onNavigate}
        />
      ) : null}
      {historial && ficha ? (
        <HistorialCompletoPanel
          open
          clienteId={ficha.clienteId}
          terminalLabel={(ficha.terminales || []).find((t) => String(t.clienteId) === String(ficha.clienteId))?.label}
          comercio={[ficha.cabecera?.nombreDeFantasia, ficha.cabecera?.cuit ? `CUIT ${formatCuit(ficha.cabecera.cuit)}` : null, ficha.cabecera?.nDeTerminal].filter(Boolean).join(" · ")}
          secciones={ficha.secciones}
          seccionInicial={historial.seccion}
          darkMode={darkMode}
          onClose={() => setHistorial(null)}
          onNavigate={onNavigate}
        />
      ) : null}
    </section>
  );
}
